/**
 * fields.tsx — primitivas de formulario compartidas por los pasos del IE.
 * Estética Stitch: labels label-caps uppercase, filled bg-[#f4f3ee] bottom-border inputs.
 */
import { Input } from "@/shared/ui/input.js";
import { DatePicker } from "@/shared/ui/date-picker.js";
import { cn } from "@/shared/lib/utils.js";

export const labelClass =
  "block text-[10px] font-bold uppercase tracking-[0.12em] text-[#474741] mb-1";

/** Texto de ayuda bajo el input — 12px, color muted. */
const hintClass = "block text-[12px] text-[#474741] mt-1";

/** Clases Tailwind del input Stitch: filled bg + bottom border only. */
const inputClass =
  "bg-[#f4f3ee] border-0 border-b border-[#c8c7bf] rounded-none px-4 py-3 h-auto " +
  "focus-visible:ring-0 focus-visible:border-[#735c00] focus-visible:bg-[#e5e2e1] " +
  "disabled:opacity-70 disabled:cursor-not-allowed";

/** Renderiza la ayuda solo si hay texto; id estable para aria-describedby. */
function Hint(props: { id: string; text?: string }) {
  if (!props.text) return null;
  return (
    <p id={props.id} className={hintClass}>
      {props.text}
    </p>
  );
}

export function LabeledField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const hintId = `${props.id}-hint`;
  return (
    <div className="grid gap-1">
      <label htmlFor={props.id} className={labelClass}>
        {props.label}
      </label>
      <Input
        id={props.id}
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        aria-describedby={props.hint ? hintId : undefined}
        className={inputClass}
      />
      <Hint id={hintId} text={props.hint} />
    </div>
  );
}

export function LabeledDate(props: {
  id: string;
  label: string;
  /** ISO `yyyy-MM-dd` o "" para vacío. */
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const hintId = `${props.id}-hint`;
  return (
    <div className="grid gap-1">
      <label htmlFor={props.id} className={labelClass}>
        {props.label}
      </label>
      <DatePicker
        id={props.id}
        value={props.value}
        onChange={props.onChange}
        placeholder={props.placeholder}
        disabled={props.disabled}
        triggerClassName={cn(
          "bg-[#f4f3ee] border-0 border-b border-[#c8c7bf] rounded-none px-4 py-3 h-auto",
          "focus-visible:ring-0 focus-visible:border-[#735c00] focus-visible:bg-[#e5e2e1]",
          "disabled:opacity-70",
        )}
      />
      <Hint id={hintId} text={props.hint} />
    </div>
  );
}

export function LabeledNumber(props: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const hintId = `${props.id}-hint`;
  return (
    <div className="grid gap-1">
      <label htmlFor={props.id} className={labelClass}>
        {props.label}
      </label>
      <Input
        id={props.id}
        type="number"
        min={0}
        value={String(props.value)}
        onChange={(e) => {
          const n = Number(e.target.value);
          props.onChange(Number.isFinite(n) ? n : 0);
        }}
        placeholder={props.placeholder}
        disabled={props.disabled}
        aria-describedby={props.hint ? hintId : undefined}
        className={inputClass}
      />
      <Hint id={hintId} text={props.hint} />
    </div>
  );
}

export function LabeledTextarea(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  rows?: number;
}) {
  const hintId = `${props.id}-hint`;
  return (
    <div className="grid gap-1">
      <label htmlFor={props.id} className={labelClass}>
        {props.label}
      </label>
      <textarea
        id={props.id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        rows={props.rows ?? 3}
        aria-describedby={props.hint ? hintId : undefined}
        className={cn(
          "flex w-full bg-[#f4f3ee] border-0 border-b border-[#c8c7bf] rounded-none px-4 py-3 text-sm",
          "placeholder:text-[#474741]/50",
          "focus-visible:outline-none focus-visible:border-[#735c00] focus-visible:bg-[#e5e2e1]",
          "disabled:cursor-not-allowed disabled:opacity-70",
          "resize-none transition-colors",
        )}
      />
      <Hint id={hintId} text={props.hint} />
    </div>
  );
}
